import React, { useEffect, useState } from "react";
import api, { API, buildWaLink, formatIDR } from "../lib/api";
import { sendWaSmart } from "../lib/wa";
import Layout from "../components/Layout";
import { Plus, Pencil, Trash2, MessageCircle, Search, Upload, Download, Lock } from "lucide-react";
import { toast } from "sonner";

const empty = { name: "", phone: "", nik: "", base_location: "", join_date: "", contract_end_date: "", role: "teknisi", salary: 0, benefit_absen: 0, benefit_kesehatan: 0, benefit_bonus: 0, benefit_transport: 0, bonus_per_point: 0, benefit_makan_siang: 0, benefit_service_motor: 0, benefit_luar_kota: 0, lembur_per_jam: 0, bonus_per_video: 0, bonus_per_review: 0, potongan_gaji: 0, absen_reminder: false, outside_base: false, outside_base_start: "", outside_base_end: "", bank_accounts: [{ bank: "", number: "", holder: "" }, { bank: "", number: "", holder: "" }] };
const twoAccounts = (accs) => [0, 1].map((i) => ({ bank: accs?.[i]?.bank || "", number: accs?.[i]?.number || "", holder: accs?.[i]?.holder || "" }));

const Technicians = () => {
  const [list, setList] = useState([]);
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [locked, setLocked] = useState(true);
  const [lockPwd, setLockPwd] = useState("");
  const [unlocking, setUnlocking] = useState(false);

  const load = async () => {
    const { data } = await api.get("/technicians", { params: { search } });
    setList(data);
  };

  useEffect(() => {
    if (locked) return;
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search, locked]);

  const submitUnlock = async (e) => {
    e.preventDefault();
    setUnlocking(true);
    try {
      await api.post("/settings/unlock", { password: lockPwd });
      setLocked(false);
      setLockPwd("");
      toast.success("Akses teknisi terbuka");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Password salah");
    } finally {
      setUnlocking(false);
    }
  };

  const openCreate = () => { setForm(empty); setEditingId(null); setModal(true); };
  const openEdit = (t) => {
    setForm({
      name: t.name || "", phone: t.phone || "",
      nik: t.nik || "",
      base_location: t.base_location || "",
      join_date: t.join_date || "",
      contract_end_date: t.contract_end_date || "",
      role: t.role || "teknisi",
      salary: t.salary || 0,
      benefit_absen: t.benefit_absen || 0,
      benefit_kesehatan: t.benefit_kesehatan || 0,
      benefit_bonus: t.benefit_bonus || 0,
      benefit_transport: t.benefit_transport || 0,
      bonus_per_point: t.bonus_per_point || 0,
      benefit_makan_siang: t.benefit_makan_siang || 0,
      benefit_service_motor: t.benefit_service_motor || 0,
      benefit_luar_kota: t.benefit_luar_kota || 0,
      lembur_per_jam: t.lembur_per_jam || 0,
      bonus_per_video: t.bonus_per_video || 0,
      bonus_per_review: t.bonus_per_review || 0,
      potongan_gaji: t.potongan_gaji || 0,
      absen_reminder: !!t.absen_reminder,
      outside_base: !!t.outside_base,
      outside_base_start: t.outside_base_start || "",
      outside_base_end: t.outside_base_end || "",
      bank_accounts: twoAccounts(t.bank_accounts),
    });
    setEditingId(t.id); setModal(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      salary: Number(form.salary) || 0,
      benefit_absen: Number(form.benefit_absen) || 0,
      benefit_kesehatan: Number(form.benefit_kesehatan) || 0,
      benefit_bonus: Number(form.benefit_bonus) || 0,
      benefit_transport: Number(form.benefit_transport) || 0,
      bonus_per_point: Number(form.bonus_per_point) || 0,
      benefit_makan_siang: Number(form.benefit_makan_siang) || 0,
      benefit_service_motor: Number(form.benefit_service_motor) || 0,
      benefit_luar_kota: Number(form.benefit_luar_kota) || 0,
      bonus_per_review: Number(form.bonus_per_review) || 0,
      potongan_gaji: Number(form.potongan_gaji) || 0,
      bank_accounts: (form.bank_accounts || []).filter((a) => (a.bank || "").trim() || (a.number || "").trim()),
    };
    try {
      if (editingId) {
        await api.put(`/technicians/${editingId}`, payload);
        toast.success("Karyawan diperbarui");
      } else {
        await api.post("/technicians", payload);
        toast.success("Karyawan ditambahkan");
      }
      setModal(false); load();
    } catch {
      toast.error("Gagal menyimpan karyawan");
    }
  };

  const remove = async () => {
    if (!confirmDel) return;
    await api.delete(`/technicians/${confirmDel.id}`);
    setConfirmDel(null);
    toast.success("Karyawan dihapus");
    load();
  };

  const sendWa = (t) => {
    const text = `Halo ${t.name}, ada koordinasi pekerjaan dari tim. Silakan cek detailnya.`;
    sendWaSmart({ phone: t.phone, text, name: t.name || "" });
  };

  const downloadTemplate = () => {
    const token = localStorage.getItem("om_token");
    fetch(`${API}/technicians/template.csv`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((b) => { const u = URL.createObjectURL(b); const a = document.createElement("a"); a.href = u; a.download = "template_teknisi.csv"; a.click(); URL.revokeObjectURL(u); });
  };

  const importCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/technicians/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.inserted} teknisi diimpor${data.errors.length ? ` (${data.errors.length} error)` : ""}`);
      load();
    } catch { toast.error("Gagal import CSV"); }
    e.target.value = "";
  };

  if (locked) {
    return (
      <Layout>
        <div className="max-w-md mx-auto mt-12 bg-white border border-[#E4E4E7]" data-testid="technicians-lock-screen">
          <div className="px-6 py-4 border-b border-[#E4E4E7]">
            <div className="label-cap">Akses Terproteksi</div>
            <h2 className="font-heading text-2xl font-bold">Data Karyawan</h2>
          </div>
          <form onSubmit={submitUnlock} className="p-6 space-y-4">
            <p className="text-sm text-[#52525B]">Halaman ini diproteksi. Masukkan password pengaturan untuk melihat & mengubah data karyawan.</p>
            <input
              type="password"
              required
              autoFocus
              value={lockPwd}
              onChange={(e) => setLockPwd(e.target.value)}
              placeholder="Password pengaturan"
              data-testid="technicians-unlock-input"
              className="w-full px-3 py-2.5 border border-[#E4E4E7] text-sm outline-none focus:border-[#002FA7] font-mono"
            />
            <button type="submit" disabled={unlocking} data-testid="technicians-unlock-button" className="w-full bg-[#002FA7] hover:bg-[#00227A] text-white text-xs font-bold uppercase tracking-wider px-6 py-3 flex items-center justify-center gap-2 disabled:opacity-60">
              <Lock size={14} /> {unlocking ? "Memverifikasi…" : "Buka Data Karyawan"}
            </button>
          </form>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="label-cap mb-2">Direktori</div>
          <h1 className="font-heading text-4xl font-black tracking-tight">Karyawan</h1>
        </div>
        <div className="flex gap-2">
          <button onClick={downloadTemplate} data-testid="technician-csv-template" className="text-xs font-bold uppercase tracking-wider px-3 py-2.5 border border-[#E4E4E7] hover:bg-[#F4F4F5] flex items-center gap-2">
            <Download size={14} /> Template CSV
          </button>
          <label className="text-xs font-bold uppercase tracking-wider px-3 py-2.5 border border-[#E4E4E7] hover:bg-[#F4F4F5] flex items-center gap-2 cursor-pointer">
            <Upload size={14} /> Import CSV
            <input type="file" accept=".csv" onChange={importCsv} className="hidden" data-testid="technician-csv-upload" />
          </label>
          <button onClick={openCreate} data-testid="add-technician-button" className="bg-[#002FA7] hover:bg-[#00227A] text-white text-xs font-bold uppercase tracking-wider px-4 py-2.5 flex items-center gap-2">
            <Plus size={14} /> Tambah Karyawan
          </button>
        </div>
      </div>

      <div className="bg-white border border-[#E4E4E7]">
        <div className="px-4 py-3 border-b border-[#E4E4E7] flex items-center gap-2">
          <Search size={14} className="text-[#52525B]" />
          <input
            data-testid="technician-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cari nama, telepon, atau base lokasi…"
            className="flex-1 outline-none text-sm bg-transparent"
          />
        </div>
        <table className="w-full">
          <thead>
            <tr className="border-b border-[#E4E4E7]">
              <th className="text-left label-cap px-4 py-3">Nama</th>
              <th className="text-left label-cap px-4 py-3">Peran</th>
              <th className="text-left label-cap px-4 py-3">NIK</th>
              <th className="text-left label-cap px-4 py-3">Telepon</th>
              <th className="text-left label-cap px-4 py-3">Base Lokasi</th>
              <th className="text-left label-cap px-4 py-3">Tgl Masuk</th>
              <th className="text-right label-cap px-4 py-3">Gaji</th>
              <th className="text-right label-cap px-4 py-3">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 && (
              <tr><td colSpan="8" className="p-8 text-center text-sm text-[#52525B]">Belum ada karyawan.</td></tr>
            )}
            {list.map((t) => (
              <tr key={t.id} className="border-b border-[#E4E4E7] row-hover" data-testid={`technician-row-${t.id}`}>
                <td className="px-4 py-3 text-sm font-bold">
                  {t.name}
                  {t.absen_reminder && <span title="Pengingat absen otomatis aktif (08:15 WIB)" data-testid={`technician-reminder-badge-${t.id}`} className="ml-1.5 text-[10px] text-[#16A34A]">⏰</span>}
                </td>
                <td className="px-4 py-3">
                  <span data-testid={`technician-role-${t.id}`} className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 ${(t.role || "teknisi") === "teknisi" ? "bg-[#002FA7]/10 text-[#002FA7]" : "bg-[#52525B]/10 text-[#52525B]"}`}>
                    {(t.role || "teknisi") === "teknisi" ? "Teknisi" : "Staff"}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm font-mono text-[#52525B]">{t.nik || "—"}</td>
                <td className="px-4 py-3 text-sm font-mono">{t.phone}</td>
                <td className="px-4 py-3 text-sm text-[#52525B]">
                  {t.base_location}
                  {t.outside_base && t.outside_base_end && t.outside_base_end >= new Date(Date.now() + 7 * 3600e3).toISOString().slice(0, 10) && (
                    <span className="block text-[10px] font-bold text-[#7C3AED]" data-testid={`technician-outside-base-${t.id}`}>Luar base s/d {t.outside_base_end}</span>
                  )}
                </td>
                <td className="px-4 py-3 text-sm font-mono">
                  {t.join_date}
                  {t.contract_end_date && (
                    <span className={`block text-[10px] ${new Date(t.contract_end_date) < new Date(Date.now() + 30 * 864e5) ? "text-[#DC2626] font-bold" : "text-[#52525B]"}`} data-testid={`technician-contract-end-${t.id}`}>
                      Kontrak s/d {t.contract_end_date}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-sm font-mono text-right">{formatIDR(t.salary)}</td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex gap-1">
                    <button onClick={() => sendWa(t)} title="WhatsApp" data-testid={`technician-wa-${t.id}`} className="p-2 hover:bg-[#059669]/10 text-[#059669] border border-[#E4E4E7]"><MessageCircle size={14} /></button>
                    <button onClick={() => openEdit(t)} title="Edit" data-testid={`technician-edit-${t.id}`} className="p-2 hover:bg-[#F4F4F5] border border-[#E4E4E7]"><Pencil size={14} /></button>
                    <button onClick={() => setConfirmDel(t)} title="Hapus" data-testid={`technician-delete-${t.id}`} className="p-2 hover:bg-[#DC2626]/10 text-[#DC2626] border border-[#E4E4E7]"><Trash2 size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={() => setModal(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white border border-[#E4E4E7] w-full max-w-2xl max-h-[90vh] flex flex-col" data-testid="technician-form">
            <div className="px-6 py-4 border-b border-[#E4E4E7] shrink-0">
              <div className="label-cap">{editingId ? "Ubah" : "Tambah"} Data</div>
              <h2 className="font-heading text-2xl font-bold">Karyawan</h2>
            </div>
            <div className="p-6 grid grid-cols-2 gap-4 overflow-y-auto flex-1">
              <div className="col-span-2">
                <label className="label-cap block mb-1.5">Peran Karyawan<span className="text-[#DC2626]">*</span></label>
                <div className="flex gap-3">
                  {[["teknisi", "Teknisi", "Bisa dimasukkan ke jadwal order"], ["staff", "Staff", "Tidak masuk jadwal order"]].map(([v, l, d]) => (
                    <label key={v} data-testid={`technician-role-${v}`} className={`flex-1 flex items-start gap-2 border px-3 py-2.5 cursor-pointer ${form.role === v ? "border-[#002FA7] bg-[#002FA7]/5" : "border-[#E4E4E7] hover:bg-[#F4F4F5]"}`}>
                      <input type="radio" name="role" value={v} checked={form.role === v} onChange={() => setForm({ ...form, role: v })} className="mt-0.5 accent-[#002FA7]" />
                      <span>
                        <span className="block text-sm font-bold">{l}</span>
                        <span className="block text-[11px] text-[#52525B]">{d}</span>
                      </span>
                    </label>
                  ))}
                </div>
              </div>
              <Field label="Nama" required>
                <input data-testid="technician-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="w-full px-3 py-2 border border-[#E4E4E7] text-sm outline-none focus:border-[#002FA7]" />
              </Field>
              <Field label="Telepon" required>
                <input data-testid="technician-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} required placeholder="08xxxxxxxxxx" className="w-full px-3 py-2 border border-[#E4E4E7] text-sm outline-none focus:border-[#002FA7] font-mono" />
              </Field>
              <Field label="Base Lokasi">
                <input data-testid="technician-base-input" value={form.base_location} onChange={(e) => setForm({ ...form, base_location: e.target.value })} className="w-full px-3 py-2 border border-[#E4E4E7] text-sm outline-none focus:border-[#002FA7]" />
                <label className={`mt-2 flex items-start gap-2 border px-3 py-2 cursor-pointer ${form.outside_base ? "border-[#7C3AED] bg-[#7C3AED]/5" : "border-[#E4E4E7] hover:bg-[#F4F4F5]"}`}>
                  <input type="checkbox" data-testid="technician-outside-base-checkbox" checked={!!form.outside_base} onChange={(e) => setForm({ ...form, outside_base: e.target.checked, outside_base_start: e.target.checked && !form.outside_base_start ? new Date(Date.now() + 7 * 3600e3).toISOString().slice(0, 10) : form.outside_base_start })} className="mt-0.5 accent-[#7C3AED]" />
                  <span className="text-xs">
                    <span className="font-bold">Penempatan luar Base Lokasi</span>
                    <span className="block text-[10px] text-[#52525B]">Di payroll, setiap hari dihitung Luar Kota = YA (kecuali libur) sampai tanggal akhir penempatan.</span>
                  </span>
                </label>
                {form.outside_base && (
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <div>
                      <span className="block text-[10px] text-[#52525B] mb-1">Mulai penempatan</span>
                      <input type="date" data-testid="technician-outside-base-start" value={form.outside_base_start} onChange={(e) => setForm({ ...form, outside_base_start: e.target.value })} className="w-full px-2 py-1.5 border border-[#E4E4E7] text-sm outline-none focus:border-[#7C3AED] font-mono" />
                    </div>
                    <div>
                      <span className="block text-[10px] text-[#52525B] mb-1">Akhir penempatan *</span>
                      <input type="date" required data-testid="technician-outside-base-end" value={form.outside_base_end} min={form.outside_base_start || undefined} onChange={(e) => setForm({ ...form, outside_base_end: e.target.value })} className="w-full px-2 py-1.5 border border-[#7C3AED]/50 text-sm outline-none focus:border-[#7C3AED] font-mono" />
                    </div>
                  </div>
                )}
              </Field>
              <Field label="NIK (absensi.deteksipipabocor.com)">
                <input data-testid="technician-nik-input" value={form.nik} onChange={(e) => setForm({ ...form, nik: e.target.value })} placeholder="EMP001 / kosongkan jika tidak ada" className="w-full px-3 py-2 border border-[#E4E4E7] text-sm outline-none focus:border-[#002FA7] font-mono" />
                <label className={`mt-2 flex items-start gap-2 border px-3 py-2 cursor-pointer ${form.absen_reminder ? "border-[#16A34A] bg-[#16A34A]/5" : "border-[#E4E4E7] hover:bg-[#F4F4F5]"}`}>
                  <input type="checkbox" data-testid="technician-absen-reminder-checkbox" checked={!!form.absen_reminder} onChange={(e) => setForm({ ...form, absen_reminder: e.target.checked })} className="mt-0.5 accent-[#16A34A]" />
                  <span className="text-xs">
                    <span className="font-bold">Pengingat Absen Otomatis (WA)</span>
                    <span className="block text-[10px] text-[#52525B]">Dikirim jam 08:15 WIB via WAHA — hanya bila belum absen & tidak libur hari itu.</span>
                  </span>
                </label>
              </Field>
              <Field label="Tanggal Masuk" required>
                <input type="date" data-testid="technician-join-input" value={form.join_date} onChange={(e) => setForm({ ...form, join_date: e.target.value })} required className="w-full px-3 py-2 border border-[#E4E4E7] text-sm outline-none focus:border-[#002FA7] font-mono" />
              </Field>
              <Field label="Tanggal Habis Kontrak">
                <input type="date" data-testid="technician-contract-end-input" value={form.contract_end_date} onChange={(e) => setForm({ ...form, contract_end_date: e.target.value })} className="w-full px-3 py-2 border border-[#E4E4E7] text-sm outline-none focus:border-[#002FA7] font-mono" />
                <p className="text-[10px] text-[#52525B] mt-1">Kosongkan jika karyawan tetap / tanpa kontrak.</p>
              </Field>
              <Field label="Gaji Pokok (IDR)" colSpan>
                <input type="number" data-testid="technician-salary-input" value={form.salary} onChange={(e) => setForm({ ...form, salary: e.target.value })} className="w-full px-3 py-2 border border-[#E4E4E7] text-sm outline-none focus:border-[#002FA7] font-mono" />
              </Field>
              <div className="col-span-2 border-t border-[#E4E4E7] pt-4" data-testid="technician-bank-section">
                <div className="label-cap mb-3">Rekening Bank (maks. 2)</div>
                <div className="space-y-3">
                  {(form.bank_accounts || twoAccounts([])).map((a, i) => {
                    const setAcc = (k, v) => setForm({ ...form, bank_accounts: (form.bank_accounts || twoAccounts([])).map((x, j) => (j === i ? { ...x, [k]: v } : x)) });
                    return (
                      <div key={i} className="grid grid-cols-3 gap-2">
                        <div>
                          <span className="block text-[10px] text-[#52525B] mb-1">Bank {i + 1}</span>
                          <input data-testid={`technician-bank-name-${i}`} value={a.bank} onChange={(e) => setAcc("bank", e.target.value)} placeholder="BCA / BRI / Mandiri…" className="w-full px-3 py-2 border border-[#E4E4E7] text-sm outline-none focus:border-[#002FA7]" />
                        </div>
                        <div>
                          <span className="block text-[10px] text-[#52525B] mb-1">No. Rekening</span>
                          <input data-testid={`technician-bank-number-${i}`} value={a.number} onChange={(e) => setAcc("number", e.target.value)} inputMode="numeric" placeholder="1234567890" className="w-full px-3 py-2 border border-[#E4E4E7] text-sm outline-none focus:border-[#002FA7] font-mono" />
                        </div>
                        <div>
                          <span className="block text-[10px] text-[#52525B] mb-1">Atas Nama (opsional)</span>
                          <input data-testid={`technician-bank-holder-${i}`} value={a.holder} onChange={(e) => setAcc("holder", e.target.value)} className="w-full px-3 py-2 border border-[#E4E4E7] text-sm outline-none focus:border-[#002FA7]" />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="col-span-2 border-t border-[#E4E4E7] pt-4">
                <div className="label-cap mb-3">Benefit Tambahan (per teknisi)</div>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Uang Absen Pagi">
                    <input type="number" data-testid="technician-benefit-absen" value={form.benefit_absen} onChange={(e) => setForm({ ...form, benefit_absen: e.target.value })} className="w-full px-3 py-2 border border-[#E4E4E7] text-sm font-mono" placeholder="20000" />
                  </Field>
                  <Field label="Uang Kesehatan">
                    <input type="number" data-testid="technician-benefit-kesehatan" value={form.benefit_kesehatan} onChange={(e) => setForm({ ...form, benefit_kesehatan: e.target.value })} className="w-full px-3 py-2 border border-[#E4E4E7] text-sm font-mono" placeholder="100000" />
                  </Field>
                  <Field label="Bonus">
                    <input type="number" data-testid="technician-benefit-bonus" value={form.benefit_bonus} onChange={(e) => setForm({ ...form, benefit_bonus: e.target.value })} className="w-full px-3 py-2 border border-[#E4E4E7] text-sm font-mono" placeholder="0" />
                  </Field>
                  <Field label="Penggantian Transport">
                    <input type="number" data-testid="technician-benefit-transport" value={form.benefit_transport} onChange={(e) => setForm({ ...form, benefit_transport: e.target.value })} className="w-full px-3 py-2 border border-[#E4E4E7] text-sm font-mono" placeholder="50000" />
                  </Field>
                </div>
                <div className="mt-4 label-cap mb-3">Komponen Penggajian (untuk hitung gaji otomatis)</div>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Bonus per Point (IDR)">
                    <input type="number" data-testid="technician-bonus-per-point" value={form.bonus_per_point} onChange={(e) => setForm({ ...form, bonus_per_point: e.target.value })} className="w-full px-3 py-2 border border-[#E4E4E7] text-sm font-mono" placeholder="10000" />
                  </Field>
                  <Field label="Uang Makan Siang">
                    <input type="number" data-testid="technician-makan-siang" value={form.benefit_makan_siang} onChange={(e) => setForm({ ...form, benefit_makan_siang: e.target.value })} className="w-full px-3 py-2 border border-[#E4E4E7] text-sm font-mono" placeholder="25000" />
                  </Field>
                  <Field label="Penggantian Service Motor">
                    <input type="number" data-testid="technician-service-motor" value={form.benefit_service_motor} onChange={(e) => setForm({ ...form, benefit_service_motor: e.target.value })} className="w-full px-3 py-2 border border-[#E4E4E7] text-sm font-mono" placeholder="50000" />
                  </Field>
                  <Field label="Tunjangan Luar Kota">
                    <input type="number" data-testid="technician-luar-kota" value={form.benefit_luar_kota} onChange={(e) => setForm({ ...form, benefit_luar_kota: e.target.value })} className="w-full px-3 py-2 border border-[#E4E4E7] text-sm font-mono" placeholder="100000" />
                  </Field>
                  <Field label="Lembur per Jam (Staff)">
                    <input type="number" data-testid="technician-lembur-per-jam" value={form.lembur_per_jam} onChange={(e) => setForm({ ...form, lembur_per_jam: e.target.value })} className="w-full px-3 py-2 border border-[#E4E4E7] text-sm font-mono" placeholder="12000" />
                  </Field>
                  <Field label="Bonus per Video (Staff)">
                    <input type="number" data-testid="technician-bonus-per-video" value={form.bonus_per_video} onChange={(e) => setForm({ ...form, bonus_per_video: e.target.value })} className="w-full px-3 py-2 border border-[#E4E4E7] text-sm font-mono" placeholder="6000" />
                  </Field>
                  <Field label="Bonus per Review Google">
                    <input type="number" data-testid="technician-bonus-per-review" value={form.bonus_per_review} onChange={(e) => setForm({ ...form, bonus_per_review: e.target.value })} className="w-full px-3 py-2 border border-[#E4E4E7] text-sm font-mono" placeholder="5000" title="Dikali jumlah review Google Bisnis baru per bulan (rekap akhir bulan − bulan sebelumnya, semua lokasi)" />
                  </Field>
                  <Field label="Potongan Gaji">
                    <input type="number" data-testid="technician-potongan-gaji" value={form.potongan_gaji} onChange={(e) => setForm({ ...form, potongan_gaji: e.target.value })} className="w-full px-3 py-2 border border-[#E4E4E7] text-sm font-mono text-[#DC2626]" placeholder="0" />
                  </Field>
                </div>
                <div className="mt-3 flex items-center justify-between bg-[#F4F4F5] px-3 py-2 border border-[#E4E4E7]">
                  <span className="label-cap">Total Take-Home (Gaji + Benefit)</span>
                  <span className="font-mono font-bold text-base" data-testid="technician-total-takehome">{formatIDR((Number(form.salary)||0) + (Number(form.benefit_absen)||0) + (Number(form.benefit_kesehatan)||0) + (Number(form.benefit_bonus)||0) + (Number(form.benefit_transport)||0))}</span>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-[#E4E4E7] flex justify-end gap-2 shrink-0">
              <button type="button" onClick={() => setModal(false)} className="px-4 py-2 text-xs font-bold uppercase border border-[#E4E4E7]">Batal</button>
              <button type="submit" data-testid="technician-submit-button" className="px-4 py-2 text-xs font-bold uppercase bg-[#002FA7] text-white hover:bg-[#00227A]">Simpan</button>
            </div>
          </form>
        </div>
      )}

      {confirmDel && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={() => setConfirmDel(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white border border-[#E4E4E7] max-w-sm w-full p-6">
            <h3 className="font-heading text-lg font-bold mb-2">Hapus Karyawan?</h3>
            <p className="text-sm text-[#52525B] mb-6">"{confirmDel.name}" akan dihapus permanen.</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirmDel(null)} className="px-4 py-2 text-xs font-bold uppercase border border-[#E4E4E7]">Batal</button>
              <button onClick={remove} data-testid="confirm-delete-technician-button" className="px-4 py-2 text-xs font-bold uppercase bg-[#DC2626] text-white">Hapus</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

const Field = ({ label, children, required, colSpan }) => (
  <div className={colSpan ? "col-span-2" : ""}>
    <label className="label-cap block mb-1.5">{label}{required && <span className="text-[#DC2626]">*</span>}</label>
    {children}
  </div>
);

export default Technicians;
